import { createFileRoute } from '@tanstack/react-router'
import { FileText, Loader2, Download, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAuditLogs,
  useAuditLogDetail,
  useExportAuditLogs,
  AUDIT_ACTION_LABELS,
  AUDIT_MODULE_LABELS,
  type AuditLogFilters,
  type AuditAction,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/settings/audit-logs' as never)({
  component: AuditLogsPage,
})

function AuditLogsPage() {
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 20,
  })
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)

  const { data, isLoading, error } = useAuditLogs(filters)
  const exportLogs = useExportAuditLogs()

  const handleFilterChange = (key: keyof AuditLogFilters, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
      page: 1,
    }))
  }

  const handleExport = () => {
    exportLogs.mutate({
      action: filters.action,
      module: filters.module,
      startDate: filters.startDate,
      endDate: filters.endDate,
    })
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载日志失败: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">审计日志</h1>
            <p className="text-muted-foreground">查看系统操作记录</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exportLogs.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {exportLogs.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          导出 CSV
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border bg-card p-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">操作类型</label>
            <Select value={filters.action ?? 'all'} onValueChange={(v: string) => handleFilterChange('action', v as AuditAction)}>
              <SelectTrigger><SelectValue placeholder="全部" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {Object.entries(AUDIT_ACTION_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">模块</label>
            <Select value={filters.module ?? 'all'} onValueChange={(v: string) => handleFilterChange('module', v)}>
              <SelectTrigger><SelectValue placeholder="全部" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {Object.entries(AUDIT_MODULE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">开始日期</label>
            <input
              type="date"
              value={filters.startDate ?? ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">结束日期</label>
            <input
              type="date"
              value={filters.endDate ?? ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">时间</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">操作人</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">模块</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">摘要</th>
                    <th className="w-16 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="h-32 text-center text-muted-foreground">
                        暂无审计日志
                      </td>
                    </tr>
                  ) : (
                    data?.data.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-muted/30">
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {new Date(log.createdAt).toLocaleString('zh-CN')}
                        </td>
                        <td className="px-4 py-3 text-sm">{log.employeeName}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="rounded bg-muted px-2 py-0.5 text-xs">
                            {AUDIT_ACTION_LABELS[log.action] ?? log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {AUDIT_MODULE_LABELS[log.module] ?? log.module}
                        </td>
                        <td className="max-w-xs truncate px-4 py-3 text-sm">{log.summary}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedLogId(log.id)}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {data && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  共 {data.meta.total} 条，第 {data.meta.page} / {data.meta.totalPages} 页
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    disabled={data.meta.page <= 1}
                    className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    上一页
                  </button>
                  <button
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    disabled={data.meta.page >= data.meta.totalPages}
                    className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  >
                    下一页
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedLogId && <AuditLogDetailDialog logId={selectedLogId} onClose={() => setSelectedLogId(null)} />}
    </div>
  )
}

function AuditLogDetailDialog({ logId, onClose }: { logId: string; onClose: () => void }) {
  const { data: log, isLoading } = useAuditLogDetail(logId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-lg bg-background p-6 shadow-lg">
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h2 className="text-lg font-semibold">审计日志详情</h2>
            <p className="text-sm text-muted-foreground">查看操作的详细信息</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : log ? (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">操作时间</p>
                <p className="font-medium">{new Date(log.createdAt).toLocaleString('zh-CN')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">操作人</p>
                <p className="font-medium">{log.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">操作类型</p>
                <p className="font-medium">{AUDIT_ACTION_LABELS[log.action] ?? log.action}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">模块</p>
                <p className="font-medium">{AUDIT_MODULE_LABELS[log.module] ?? log.module}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">目标类型</p>
                <p className="font-medium">{log.targetType ?? '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">目标 ID</p>
                <p className="font-mono text-sm">{log.targetId ?? '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">摘要</p>
              <p className="font-medium">{log.summary ?? '-'}</p>
            </div>
            {(log.oldValue !== undefined || log.newValue !== undefined) && (
              <div>
                <p className="text-sm text-muted-foreground">变更数据</p>
                <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify({ oldValue: log.oldValue ?? null, newValue: log.newValue ?? null }, null, 2)}
                </pre>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">IP 地址</p>
                <p className="font-mono">{log.ipAddress ?? '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">请求 ID</p>
                <p className="font-mono text-xs">{log.requestId ?? '-'}</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
